/**
 * VOS Movimento · os tokens (aprovado pelo founder em 26/09 e aplicado no app
 * inteiro; laboratório vivo em /movimento-lab).
 *
 * Três palavras guiam o sistema: RÁPIDO, CONTÍNUO, DISCRETO. O VOS é
 * ferramenta de trabalho aberta o dia inteiro: o movimento explica (de onde
 * veio, pra onde foi, o que mudou), confirma (deu certo) e mantém a
 * continuidade (o mesmo objeto num lugar novo). Nunca enfeita.
 *
 * Tudo que se move lê daqui. Nenhuma duração, curva ou mola é escrita no
 * componente: tempo novo nasce aqui, com nome. O CSS espelha os mesmos
 * valores em `--vos-dur-*`, `--vos-curva-*` e `--vos-atraso-*`, e o
 * `packages/ui/tokens.json` guarda a cópia legível por máquina.
 *
 * O Motion fala em SEGUNDOS; o CSS em milissegundos.
 */

/**
 * Cinco degraus de duração. Quanto maior a superfície e a distância, maior o
 * degrau; nada de trabalho passa de 280ms (o `xl` é só pra gráfico na primeira
 * pintura, progresso e momento de sucesso).
 */
export const DUR = {
  /** 100 · hover, aperto, cor, foco, ícone pequeno. */
  xs: 0.1,
  /** 150 · flutuantes (menu, popover, select, tooltip), chip, troca de ícone e de rótulo. */
  sm: 0.15,
  /** 200 · modal, toast, linha, card, acordeão, erro de campo, conteúdo de aba. */
  md: 0.2,
  /** 280 · gaveta, faixa lateral, página, folha de baixo, layout grande. */
  lg: 0.28,
  /** 420 · gráfico na 1ª pintura, progresso, contagem, momento de sucesso. */
  xl: 0.42,
} as const;

/**
 * Saída ≈ 70% da entrada: quem sai não precisa ser lido, e a tela fica livre
 * mais cedo. Toda saída usa a `CURVA.saida`.
 */
export const DUR_SAIDA = {
  xs: 0.07,
  sm: 0.1,
  md: 0.14,
  lg: 0.2,
  xl: 0.3,
} as const;

export type Degrau = keyof typeof DUR;

/** O aperto desce em 80ms: rápido o bastante pra o dedo sentir na hora. */
export const DUR_APERTO = 0.08;

/**
 * Ciclos CONTÍNUOS, só enquanto algo trabalha de verdade (Zé pensando, upload,
 * alguém digitando). Parou o trabalho, o ciclo para. Segundos por volta.
 */
export const CICLO = {
  /** Girador pequeno dentro de rótulo e botão. */
  girador: 0.7,
  /** Os três pontos de "digitando". */
  digitando: 1.2,
  /** O Zé respira enquanto pensa (1 → 1.04 → 1). */
  respiro: 1.8,
  /** Brilho que varre o esqueleto e a barra indeterminada. */
  varredura: 1.6,
  /** Brilho do texto "Pensando". */
  brilho: 2,
} as const;

/**
 * O OLÁ do onboarding (founder 02/11: "como o onboarding da Apple no
 * iPhone"). É palco, não trabalho: o único lugar do app onde o tempo é lento
 * de propósito. Cada língua fica um pouco na tela, a troca é macia e o fundo
 * deriva devagar. No reduzido, o olá chega pronto e o fundo fica parado.
 */
export const OLA = {
  /** Quanto cada língua fica na tela, já contando a troca (s). */
  palavra: 1.4,
  /** A troca de uma língua pra outra: some subindo, a nova chega de baixo (s). */
  troca: 0.7,
} as const;

/** Gesto com a mão: quando soltar fecha, e quando um peteleco conta. */
export const GESTO = {
  /** Folha de baixo e gaveta arrastadas: passou disto (px), fecha. */
  distanciaPraFechar: 120,
  /** Ou soltou mais rápido que isto (px/s), fecha. */
  velocidadePraFechar: 600,
  /** Linha deslizada: peteleco a partir disto (px/s) vale como decisão. */
  peteleco: 400,
  /**
   * No toque, SEGURAR pega (s): o dedo parado este tempo vira arraste (bloco
   * da agenda, card do quadro, conversa). Antes disso o dedo é rolagem. É
   * intenção de gesto, não tempo de animação: não entra na câmera lenta.
   */
  segurar: 0.5,
} as const;

/**
 * Duas curvas e a reta. A de ENTRADA é a assinatura do VOS (`easeVos`,
 * inalterada): chega rápido e pousa. A de SAÍDA é a de 01/09 ("sai rápido,
 * mas solta em vez de rasgar"). A reta só vale pra progresso contínuo.
 */
export const CURVA = {
  entrada: [0.22, 1, 0.36, 1],
  saida: [0.4, 0, 0.6, 1],
  continua: "linear",
} as const;

/**
 * Molas, descritas pelo tempo que o olho vê (`visualDuration`) e pelo quique
 * (`bounce`). Mola é pra quem MUDA DE LUGAR na tela e pode ser interrompido no
 * meio (clicar em outra aba antes do cursor chegar redireciona, não recomeça).
 *
 * `bounce` só existe no `pouso`: o card que a pessoa soltou e o momento de
 * sucesso. Em todo o resto o VOS não quica.
 */
export const MOLA = {
  /** Régua ativa da lateral, sublinhado de aba, cursor do segmentado, seleção. */
  indicador: { type: "spring", visualDuration: 0.24, bounce: 0 },
  /** Quem se reorganiza: lista, card, coluna, painel que abre espaço. */
  layout: { type: "spring", visualDuration: 0.3, bounce: 0 },
  /** Soltar o card no Kanban, momento de sucesso. O ÚNICO com quique. */
  pouso: { type: "spring", visualDuration: 0.36, bounce: 0.22 },
  /** Número, barra de progresso, troca de rótulo no mesmo lugar. */
  suave: { type: "spring", visualDuration: 0.42, bounce: 0 },
  /** Fim de gesto com a mão (folha de baixo, deslizar linha): herda a velocidade do dedo. */
  gesto: { type: "spring", visualDuration: 0.26, bounce: 0.08 },
} as const;

export type NomeDaMola = keyof typeof MOLA;

/** Distâncias em px. Quem dá a sensação de "veio de lá" é a curva, não a distância. */
export const DESLOC = {
  /** Convite: a seta do botão anda no hover. */
  toque: 2,
  /** Flutuante sai da borda do gatilho: menu, popover, select, tooltip. */
  flutuante: 4,
  /** Linha, card, toast, mensagem, erro de campo, conteúdo de página. */
  conteudo: 8,
  /** Troca de vista com sentido (lista → conversa, passo do wizard, voltar). */
  vista: 24,
} as const;

/** Escalas de partida e de gesto. */
export const ESCALA = {
  /** O botão afunda ao apertar (era .985: não se sentia). */
  aperto: 0.97,
  /** Card clicável afunda menos que botão. */
  apertoCard: 0.99,
  /** Menu, popover, select: nascem 2% menores, do lado do gatilho. */
  flutuante: 0.98,
  /** Tooltip: balão pequeno pede um pouco mais pra ser visto. */
  tooltip: 0.96,
  /** Modal (pedido do founder em 26/09: .97 → 1). */
  modal: 0.97,
  /** Quem sai encolhe 2%. */
  saida: 0.98,
  /** Chip e filtro que entram ou saem. */
  chip: 0.9,
  /** O card levantado pela mão (1.03 → 1.02 em 27/09: o card pesava na mão). */
  levanta: 1.02,
  /** A linha de lista levantada pra reordenar (mais discreta que o card). */
  levantaLinha: 1.01,
  /** O respiro do Zé enquanto pensa (o único ciclo de escala). */
  respiro: 1.04,
  /** A parte em foco de um gráfico (fatia, barra) cresce 3% do centro. */
  destaque: 1.03,
} as const;

/** Opacidades de estado. */
export const OPACIDADE = {
  /** O resto do gráfico quando uma parte está em foco. */
  apagada: 0.45,
  /** A mensagem que ainda está sendo enviada. */
  enviando: 0.6,
} as const;

/** Rotação do card na mão (graus): o suficiente pra dizer "solto". */
export const INCLINA = { arraste: 1.5 } as const;

/** Atrasos em segundos. */
export const ATRASO = {
  /** Tooltip espera a intenção; nunca aparece de estalo. */
  tooltip: 0.35,
  /** Depois que um tooltip abriu, o vizinho abre na hora por esta janela. */
  tooltipJanela: 0.5,
  /** Submenu: intenção de hover antes de abrir. */
  submenu: 0.12,
  /** Submenu: tolerância antes de fechar quando o ponteiro sai (atravessar a diagonal). */
  submenuFecha: 0.16,
  /** Vida de um aviso com ação (toast, "Desfazer"): tempo de ler e decidir. */
  aviso: 4,
  /** Esqueleto só aparece se o dado demorar mais que isto (`--vos-skel-delay`). */
  esqueleto: 0.28,
  /** Depois de aparecer, o esqueleto fica pelo menos isto: nada de lampejo cinza. */
  esqueletoMinimo: 0.3,
  /** "Salvo ✓" fica na tela antes de voltar ao rótulo de origem. */
  desfecho: 1.4,
  /** O modal que fecha quando dá certo segura o "Salvo ✓" isto antes de fechar (founder 26/09). */
  okAntesDeFechar: 0.7,
  /** Prévia no hover (o evento da Agenda): quase na hora, mas sem piscar em cada item que o mouse cruza. */
  previa: 0.15,
  /** Conteúdo da gaveta chega depois do painel começar a andar. */
  conteudoDaGaveta: 0.06,
} as const;

/** Cascata: cada item espera `passo`, e ela para de crescer no `teto`. */
export const CASCATA = {
  passo: 0.025,
  teto: 10,
  /** Barras de gráfico: um pouco mais espaçadas, teto maior. */
  passoGrafico: 0.03,
  tetoGrafico: 12,
} as const;

/** Tempo do atraso de cascata do item `i` (0-based), já com o teto. */
export function atrasoDaCascata(i: number, passo: number = CASCATA.passo, teto: number = CASCATA.teto) {
  return Math.min(Math.max(i, 0), teto) * passo;
}

/**
 * Espelho CSS dos tokens: o nome da custom property e o valor em ms. É o que
 * o `vos-foundation.css` declara e o que o laboratório imprime na aba Base.
 */
export const TOKENS_CSS = {
  "--vos-dur-xs": `${DUR.xs * 1000}ms`,
  "--vos-dur-sm": `${DUR.sm * 1000}ms`,
  "--vos-dur-md": `${DUR.md * 1000}ms`,
  "--vos-dur-lg": `${DUR.lg * 1000}ms`,
  "--vos-dur-xl": `${DUR.xl * 1000}ms`,
  "--vos-dur-saida-xs": `${DUR_SAIDA.xs * 1000}ms`,
  "--vos-dur-saida-sm": `${DUR_SAIDA.sm * 1000}ms`,
  "--vos-dur-saida-md": `${DUR_SAIDA.md * 1000}ms`,
  "--vos-dur-saida-lg": `${DUR_SAIDA.lg * 1000}ms`,
  "--vos-curva-entrada": `cubic-bezier(${CURVA.entrada.join(", ")})`,
  "--vos-curva-saida": `cubic-bezier(${CURVA.saida.join(", ")})`,
  "--vos-dur-aperto": `${DUR_APERTO * 1000}ms`,
  "--vos-atraso-tooltip": `${ATRASO.tooltip * 1000}ms`,
  "--vos-skel-delay": `${ATRASO.esqueleto * 1000}ms`,
  "--vos-desloc-toque": `${DESLOC.toque}px`,
  "--vos-desloc-flutuante": `${DESLOC.flutuante}px`,
  "--vos-desloc-conteudo": `${DESLOC.conteudo}px`,
  "--vos-desloc-vista": `${DESLOC.vista}px`,
  "--vos-escala-aperto": String(ESCALA.aperto),
  "--vos-escala-flutuante": String(ESCALA.flutuante),
  "--vos-escala-saida": String(ESCALA.saida),
  "--vos-escala-destaque": String(ESCALA.destaque),
  "--vos-opacidade-apagada": String(OPACIDADE.apagada),
  "--vos-ciclo-girador": `${CICLO.girador * 1000}ms`,
  "--vos-ciclo-digitando": `${CICLO.digitando * 1000}ms`,
  "--vos-ciclo-varredura": `${CICLO.varredura * 1000}ms`,
  "--vos-ciclo-respiro": `${CICLO.respiro * 1000}ms`,
  "--vos-ciclo-brilho": `${CICLO.brilho * 1000}ms`,
} as const;
